import { z } from 'zod'
import { DateStringSchema, RegistrationSchema } from './booking'
import { RateSettingSchema, SETTINGS } from './settings'

// The club's expense claims (the acct schema, issue #226): the paper travel,
// fuel and other-expense forms, online. Keep it in step with V1670-V1720 in
// db/flyway/schema/sql.
//
// Everything the API enforces and the wizard shows live is here and pure, so
// a phone computes a claim's totals, and says what is still missing, with
// exactly the code the server will apply: money in whole cents, what a line
// may contain, who may move a claim to which status, and the hash a signature
// is made over.

export const EXPENSE_KINDS = ['travel', 'fuel', 'other'] as const
export type ExpenseKind = (typeof EXPENSE_KINDS)[number]
export const ExpenseKindSchema = z.enum(EXPENSE_KINDS)

export const EXPENSE_STATUSES = ['draft', 'submitted', 'needs_info', 'approved', 'rejected', 'paid', 'withdrawn'] as const
export type ExpenseStatus = (typeof EXPENSE_STATUSES)[number]

// A travel line is paid at a club rate (own car, the allowances) or is an
// actual cost with a receipt ('other': train, taxi, parking).
export const TRAVEL_MODES = ['car', 'allowance_full', 'allowance_partial', 'meal', 'other'] as const
export type TravelMode = (typeof TRAVEL_MODES)[number]
export const RATE_MODES = ['car', 'allowance_full', 'allowance_partial', 'meal'] as const
export type RateMode = (typeof RATE_MODES)[number]

export const EXPENSE_UNITS = ['km', 'day', 'l', 'pcs'] as const
export type ExpenseUnit = (typeof EXPENSE_UNITS)[number]

// v1 pays euros only. The tables carry a currency and a conversion for the
// foreign-currency enhancement; until then anything else is refused.
export const EXPENSE_CURRENCY = 'EUR'

export const MAX_EXPENSE_LINES = 30
export const MAX_EXPENSE_RECEIPTS = 10
export const MAX_EXPENSE_AMOUNT = 100_000

// ---------------------------------------------------------------------------
// The rates

// Euros per unit. Seeded at the Finnish Tax Administration's 2026 tax-free
// maximums for a trip in Finland (vero.fi, "Kilometrikorvaus ja päiväraha"):
// the fallback while a setting is missing, and what the admin Settings page
// starts from. New figures are published every year.
export interface ExpenseRates {
  mileage: number
  allowanceFull: number
  allowancePartial: number
  meal: number
}

export const EXPENSE_RATE_DEFAULTS: ExpenseRates = {
  mileage: 0.55,
  allowanceFull: 54,
  allowancePartial: 25,
  meal: 13.5,
}

// The four rates from their setting values, each falling back to the default
// when the setting is missing or not a valid rate: a mistyped row must not be
// able to take claim entry down (as getClubTimezone).
export function ratesFromSettings(get: (key: string) => string | null | undefined): ExpenseRates {
  const rate = (key: string, fallback: number): number => {
    const parsed = RateSettingSchema.safeParse(get(key) ?? '')
    return parsed.success ? Number(parsed.data) : fallback
  }
  return {
    mileage: rate(SETTINGS.expenseMileageRate, EXPENSE_RATE_DEFAULTS.mileage),
    allowanceFull: rate(SETTINGS.expenseDailyAllowanceFull, EXPENSE_RATE_DEFAULTS.allowanceFull),
    allowancePartial: rate(SETTINGS.expenseDailyAllowancePartial, EXPENSE_RATE_DEFAULTS.allowancePartial),
    meal: rate(SETTINGS.expenseMealAllowance, EXPENSE_RATE_DEFAULTS.meal),
  }
}

export function rateFor(mode: RateMode, rates: ExpenseRates): number {
  switch (mode) {
    case 'car':
      return rates.mileage
    case 'allowance_full':
      return rates.allowanceFull
    case 'allowance_partial':
      return rates.allowancePartial
    case 'meal':
      return rates.meal
  }
}

export const isRateMode = (mode: TravelMode | null | undefined): mode is RateMode =>
  mode != null && (RATE_MODES as readonly string[]).includes(mode)

// The unit a rate line is counted in.
const RATE_UNIT: Record<RateMode, ExpenseUnit> = {
  car: 'km',
  allowance_full: 'day',
  allowance_partial: 'day',
  meal: 'day',
}

// ---------------------------------------------------------------------------
// Money

export const toCents = (euros: number): number => Math.round(euros * 100)
export const fromCents = (cents: number): number => cents / 100

// quantity x unit price in whole cents, half a cent rounding up. BigInt so that
// neither the three decimals of a quantity nor the four of a price is ever a
// floating-point sum.
export function lineAmountCents(quantity: number, unitPrice: number): number {
  const milli = BigInt(Math.round(quantity * 1000))
  const tenThousandths = BigInt(Math.round(unitPrice * 10_000))
  return Number((milli * tenThousandths + 50_000n) / 100_000n)
}

// What one unit cost, from a receipt's total: the "a-hinta" of the fuel form.
export function unitPriceFrom(amountCents: number, quantity: number): number {
  return Math.round((amountCents / 100 / quantity) * 10_000) / 10_000
}

// ---------------------------------------------------------------------------
// Lines

// A line as the member enters it. The unit price is never an input: a line at
// a club rate takes it from the settings, an actual cost takes its amount
// from the receipt and the price follows from that.
export const ExpenseLineInputSchema = z.object({
  // Keeps a line's identity across saves; absent for a new line.
  lineId: z.string().uuid().nullish(),
  description: z.string().trim().min(1).max(200),
  incurredOn: DateStringSchema,
  startsAt: z.string().datetime({ offset: true }).nullish(),
  endsAt: z.string().datetime({ offset: true }).nullish(),
  travelMode: z.enum(TRAVEL_MODES).nullish(),
  quantity: z.number().positive().max(100_000),
  unit: z.enum(EXPENSE_UNITS),
  // The receipt's total, for an actual cost; absent on a line at a club rate.
  amount: z.number().min(0).max(MAX_EXPENSE_AMOUNT).nullish(),
})
export type ExpenseLineInput = z.infer<typeof ExpenseLineInputSchema>

export const CreateExpenseClaimSchema = z.object({ kind: ExpenseKindSchema })

// Saving a draft: whatever the member has so far. What is still missing is
// not an error until they sign (missingForSubmit).
export const UpdateExpenseClaimSchema = z.object({
  purpose: z.string().trim().max(500).nullish(),
  aircraftRegistration: RegistrationSchema.nullish(),
  lines: z.array(ExpenseLineInputSchema).max(MAX_EXPENSE_LINES).optional(),
})
export type UpdateExpenseClaim = z.infer<typeof UpdateExpenseClaimSchema>

export interface ResolvedExpenseLine {
  description: string
  incurredOn: string
  startsAt: string | null
  endsAt: string | null
  travelMode: TravelMode | null
  quantity: number
  unit: ExpenseUnit
  unitPrice: number
  amountCents: number
}

export const LINE_PROBLEMS = [
  'travel_mode_required',
  'travel_fields_not_allowed',
  'unit_mismatch',
  'quantity_not_whole',
  'amount_required',
  'amount_not_allowed',
  'ends_before_start',
  'in_future',
] as const
export type LineProblem = (typeof LINE_PROBLEMS)[number]

const ACTUAL_COST_UNITS: Record<ExpenseKind, readonly ExpenseUnit[]> = {
  travel: ['pcs'],
  fuel: ['l'],
  other: ['pcs', 'km', 'day', 'l'],
}

// Applies the rules of a claim's kind to one line and works out its price
// and amount. `today` is the club's calendar day (YYYY-MM-DD): the rule has no
// clock of its own.
export function resolveLine(
  kind: ExpenseKind,
  input: ExpenseLineInput,
  rates: ExpenseRates,
  today: string,
): { line: ResolvedExpenseLine } | { problems: LineProblem[] } {
  const problems: LineProblem[] = []
  const startsAt = input.startsAt ?? null
  const endsAt = input.endsAt ?? null
  const travelMode = input.travelMode ?? null

  if (input.incurredOn > today) problems.push('in_future')

  if (kind !== 'travel' && (travelMode !== null || startsAt !== null || endsAt !== null)) {
    problems.push('travel_fields_not_allowed')
  }
  if (startsAt !== null && endsAt !== null && Date.parse(endsAt) < Date.parse(startsAt)) {
    problems.push('ends_before_start')
  }

  let unitPrice = 0
  let amountCents = 0

  if (kind === 'travel' && travelMode === null) {
    problems.push('travel_mode_required')
  } else if (kind === 'travel' && isRateMode(travelMode)) {
    if (input.unit !== RATE_UNIT[travelMode]) problems.push('unit_mismatch')
    if (input.amount != null) problems.push('amount_not_allowed')
    if (RATE_UNIT[travelMode] === 'day' && !Number.isInteger(input.quantity)) problems.push('quantity_not_whole')
    unitPrice = rateFor(travelMode, rates)
    amountCents = lineAmountCents(input.quantity, unitPrice)
  } else {
    // An actual cost: the receipt's total is the amount.
    if (!ACTUAL_COST_UNITS[kind].includes(input.unit)) problems.push('unit_mismatch')
    if (input.amount == null) problems.push('amount_required')
    else {
      amountCents = toCents(input.amount)
      unitPrice = unitPriceFrom(amountCents, input.quantity)
    }
  }

  if (problems.length > 0) return { problems }
  return {
    line: {
      description: input.description,
      incurredOn: input.incurredOn,
      startsAt,
      endsAt,
      travelMode,
      quantity: input.quantity,
      unit: input.unit,
      unitPrice,
      amountCents,
    },
  }
}

export const claimTotalCents = (lines: Array<{ amountCents: number }>): number =>
  lines.reduce((sum, l) => sum + l.amountCents, 0)

// ---------------------------------------------------------------------------
// Times of day

// A claim stores instants and a form has the club's clock: zonedInstant (flightLog.ts)
// turns "08:30" on a date into the instant, and this turns an instant back.
// The wall-clock "HH:MM" of an instant in `timeZone`.
export function clockInZone(iso: string, timeZone: string): string {
  const parts = Object.fromEntries(
    new Intl.DateTimeFormat('en-GB', { timeZone, hourCycle: 'h23', hour: '2-digit', minute: '2-digit' })
      .formatToParts(new Date(iso))
      .map((p) => [p.type, p.value]),
  )
  return `${parts.hour}:${parts.minute}`
}

// ---------------------------------------------------------------------------
// What a claim still lacks before it can be signed

export const SUBMIT_BLOCKERS = [
  'purpose',
  'aircraft',
  'lines',
  'receipts',
  'name',
  'phone',
  'home_address',
  'iban',
  'hetu',
] as const
export type SubmitBlocker = (typeof SUBMIT_BLOCKERS)[number]

export interface SubmitProfile {
  displayName: string | null
  phone: string | null
  address: string | null
  postalCode: string | null
  city: string | null
  hasIban: boolean
  hasHetu: boolean
}

// A receipt backs an actual cost. A line paid at the club's mileage rate has
// none to show, so a claim of only those needs no receipt.
export const receiptRequired = (lines: Array<{ travelMode: TravelMode | null }>): boolean =>
  lines.some((l) => l.travelMode !== 'car')

const blank = (v: string | null | undefined): boolean => v == null || v.trim() === ''

// What the paper form asked for and the claim does not yet have: a travel
// claim wants the trip's purpose, the claimant's home address and personal
// identity code; a fuel claim the aircraft; every claim a line, a receipt (unless it
// is all mileage) and the claimant's name, phone and IBAN. The wizard asks for exactly these.
export function missingForSubmit(
  kind: ExpenseKind,
  claim: {
    purpose: string | null
    aircraftRegistration: string | null
    lineCount: number
    receiptRequired: boolean
  },
  profile: SubmitProfile,
  receiptCount: number,
): SubmitBlocker[] {
  const missing: SubmitBlocker[] = []
  if (kind === 'travel' && blank(claim.purpose)) missing.push('purpose')
  if (kind === 'fuel' && blank(claim.aircraftRegistration)) missing.push('aircraft')
  if (claim.lineCount === 0) missing.push('lines')
  if (claim.receiptRequired && receiptCount === 0) missing.push('receipts')
  if (blank(profile.displayName)) missing.push('name')
  if (blank(profile.phone)) missing.push('phone')
  if (kind === 'travel' && (blank(profile.address) || blank(profile.postalCode) || blank(profile.city))) {
    missing.push('home_address')
  }
  if (!profile.hasIban) missing.push('iban')
  if (kind === 'travel' && !profile.hasHetu) missing.push('hetu')
  return missing
}

// ---------------------------------------------------------------------------
// The statuses

export const isEditable = (status: ExpenseStatus): boolean => status === 'draft' || status === 'needs_info'

// Only a draft may be deleted: once submitted a claim, its signatures and its
// receipts are accounting records, kept for at least seven years.
export const isDeletable = (status: ExpenseStatus): boolean => status === 'draft'

export const EXPENSE_ACTIONS = ['submit', 'withdraw', 'approve', 'reject', 'request_info', 'mark_paid'] as const
export type ExpenseAction = (typeof EXPENSE_ACTIONS)[number]

export type TransitionRefusal =
  | 'not_owner'
  | 'not_processor'
  | 'own_claim'
  | 'wrong_status'
  | 'message_required'

const TRANSITIONS: Record<ExpenseAction, { from: readonly ExpenseStatus[]; to: ExpenseStatus; by: 'owner' | 'processor'; needsMessage?: true }> = {
  submit: { from: ['draft', 'needs_info'], to: 'submitted', by: 'owner' },
  withdraw: { from: ['submitted', 'needs_info'], to: 'withdrawn', by: 'owner' },
  approve: { from: ['submitted'], to: 'approved', by: 'processor' },
  reject: { from: ['submitted'], to: 'rejected', by: 'processor', needsMessage: true },
  request_info: { from: ['submitted'], to: 'needs_info', by: 'processor', needsMessage: true },
  mark_paid: { from: ['approved'], to: 'paid', by: 'processor' },
}

// The whole state machine. A processor can never decide, or pay, their own
// claim: someone else has to.
export function canTransition(input: {
  status: ExpenseStatus
  action: ExpenseAction
  actorId: string
  ownerId: string
  actorCanProcess: boolean
  message?: string | null
}): { ok: true; to: ExpenseStatus } | { ok: false; reason: TransitionRefusal } {
  const rule = TRANSITIONS[input.action]
  if (rule.by === 'owner') {
    if (input.actorId !== input.ownerId) return { ok: false, reason: 'not_owner' }
  } else {
    if (!input.actorCanProcess) return { ok: false, reason: 'not_processor' }
    if (input.actorId === input.ownerId) return { ok: false, reason: 'own_claim' }
  }
  if (!rule.from.includes(input.status)) return { ok: false, reason: 'wrong_status' }
  if (rule.needsMessage && blank(input.message)) return { ok: false, reason: 'message_required' }
  return { ok: true, to: rule.to }
}

// ---------------------------------------------------------------------------
// Signing

export const SIGNATURE_ROLES = ['claimant', 'approver'] as const
export type SignatureRole = (typeof SIGNATURE_ROLES)[number]

// How the signer proved who they are at the moment of signing: a passkey, the
// account password, or a one-time code sent to their email address.
export const SIGNATURE_METHODS = ['passkey', 'password', 'email_link'] as const
export type SignatureMethod = (typeof SIGNATURE_METHODS)[number]

// The wording of the declaration the signer is shown. A signature records the
// version, so the text can be improved without changing what an earlier
// signature meant: add a version, never edit one.
export const EXPENSE_DECLARATION_VERSION = 1

const DECLARATIONS: Record<number, Record<SignatureRole, Record<'fi' | 'en' | 'sv', string>>> = {
  1: {
    claimant: {
      fi: 'Vakuutan, että tässä kululaskussa ilmoitetut tiedot ovat oikeat ja että kulut ovat aiheutuneet Jukolan Pilotit ry:n toiminnasta. Tunnistautumalla uudelleen allekirjoitan kululaskun sähköisesti.',
      en: 'I declare that the details in this expense claim are correct and that the costs were incurred in the activities of Jukolan Pilotit ry. By confirming my identity again I sign the claim electronically.',
      sv: 'Jag försäkrar att uppgifterna i detta utlägg är riktiga och att kostnaderna har uppstått i Jukolan Pilotit ry:s verksamhet. Genom att bekräfta min identitet på nytt undertecknar jag utlägget elektroniskt.',
    },
    approver: {
      fi: 'Olen tarkastanut kululaskun ja sen liitteet ja hyväksyn kululaskun maksettavaksi. Tunnistautumalla uudelleen allekirjoitan hyväksynnän sähköisesti.',
      en: 'I have checked the claim and its receipts and approve it for payment. By confirming my identity again I sign the approval electronically.',
      sv: 'Jag har granskat utlägget och verifikaten och godkänner det för betalning. Genom att bekräfta min identitet på nytt undertecknar jag godkännandet elektroniskt.',
    },
  },
}

export function declarationText(version: number, role: SignatureRole, locale: string): string {
  const texts = DECLARATIONS[version]?.[role] ?? DECLARATIONS[EXPENSE_DECLARATION_VERSION]![role]
  return texts[locale as 'fi' | 'en' | 'sv'] ?? texts.fi
}

// Everything a signature covers. The IDs bind it to this claim and this
// signer; the receipts are the hashes of their bytes, so a file replaced after
// signing changes the hash.
export interface SignableClaim {
  claimId: string
  userId: string
  kind: ExpenseKind
  currency: string
  aircraftRegistration: string | null
  purpose: string | null
  // What the paper form showed above the lines, as it was when signed.
  claimantName: string
  claimantPhone: string | null
  homeAddress: string | null
  declarationVersion: number
  lines: Array<{
    lineNo: number
    description: string
    incurredOn: string
    startsAt: string | null
    endsAt: string | null
    travelMode: TravelMode | null
    quantity: number
    unit: ExpenseUnit
    unitPrice: number
    amountCents: number
  }>
  receipts: Array<{ sha256: string }>
}

// Sorted keys, no whitespace: the same claim serialises to the same bytes in
// the browser, in the Worker and in a test.
function stableStringify(value: unknown): string {
  if (Array.isArray(value)) return `[${value.map(stableStringify).join(',')}]`
  if (value !== null && typeof value === 'object') {
    const entries = Object.entries(value as Record<string, unknown>)
      .filter(([, v]) => v !== undefined)
      .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
    return `{${entries.map(([k, v]) => `${JSON.stringify(k)}:${stableStringify(v)}`).join(',')}}`
  }
  return JSON.stringify(value)
}

const instant = (v: string | null): string | null => (v === null ? null : new Date(v).toISOString())

// Quantities and prices are written as integers (thousandths and
// ten-thousandths), never as floating-point text.
export function canonicalClaim(claim: SignableClaim): string {
  return stableStringify({
    v: claim.declarationVersion,
    claimId: claim.claimId,
    userId: claim.userId,
    kind: claim.kind,
    currency: claim.currency,
    aircraftRegistration: claim.aircraftRegistration,
    purpose: claim.purpose,
    claimantName: claim.claimantName,
    claimantPhone: claim.claimantPhone,
    homeAddress: claim.homeAddress,
    lines: [...claim.lines]
      .sort((a, b) => a.lineNo - b.lineNo)
      .map((l) => ({
        lineNo: l.lineNo,
        description: l.description,
        incurredOn: l.incurredOn,
        startsAt: instant(l.startsAt),
        endsAt: instant(l.endsAt),
        travelMode: l.travelMode,
        quantityMilli: Math.round(l.quantity * 1000),
        unit: l.unit,
        unitPriceTenThousandths: Math.round(l.unitPrice * 10_000),
        amountCents: l.amountCents,
      })),
    receipts: claim.receipts.map((r) => r.sha256).sort(),
  })
}

// This package is compiled without the DOM or Node typings, but runs where Web
// Crypto exists (every browser, the Worker, Node 20+). Only what the hash
// uses is declared, in this module's scope.
declare const crypto: { subtle: { digest(algorithm: string, data: Uint8Array): Promise<ArrayBuffer> } }
declare class TextEncoder {
  encode(input: string): Uint8Array
}

export async function claimContentHash(claim: SignableClaim): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(canonicalClaim(claim)))
  return Array.from(new Uint8Array(digest), (b) => b.toString(16).padStart(2, '0')).join('')
}

// ---------------------------------------------------------------------------
// The API's contracts

// What the member presents to sign. A passkey signs a challenge derived from
// the content hash and `nonce` (signingProof.ts); the other two prove the
// password or the mailbox, and the hash they sign is the one in the request.
export const SignProofSchema = z.discriminatedUnion('method', [
  z.object({
    method: z.literal('passkey'),
    nonce: z.string().regex(/^[0-9a-f]{32}$/),
    response: z.record(z.string(), z.unknown()),
  }),
  z.object({ method: z.literal('password'), password: z.string().min(1).max(200) }),
  z.object({ method: z.literal('email_link'), code: z.string().regex(/^\d{6}$/) }),
])
export type SignProof = z.infer<typeof SignProofSchema>

// Asked for before signing: a passkey gets its options, an emailed code gets
// sent. The password needs nothing.
export const SignChallengeRequestSchema = z.object({ method: z.enum(['passkey', 'email_link']) })
export type SignChallengeRequest = z.infer<typeof SignChallengeRequestSchema>

export const SubmitExpenseSchema = z.object({
  // The hash the member was shown. Refused if the claim has changed since.
  contentHash: z.string().regex(/^[0-9a-f]{64}$/),
  proof: SignProofSchema,
})
export type SubmitExpense = z.infer<typeof SubmitExpenseSchema>

export const ExpenseMessageSchema = z.object({ message: z.string().trim().min(1).max(2000) })

export interface SignChallengeResponse {
  contentHash: string
  // Passkey only: the nonce to send back, and the WebAuthn request options.
  nonce?: string
  options?: Record<string, unknown>
}

export interface ExpenseLineView {
  lineId: string
  lineNo: number
  description: string
  incurredOn: string
  startsAt: string | null
  endsAt: string | null
  travelMode: TravelMode | null
  quantity: number
  unit: ExpenseUnit
  unitPrice: number
  amount: number
}

export interface ExpenseReceiptView {
  receiptId: string
  lineId: string | null
  filename: string
  contentType: string
  sizeBytes: number
  uploadedAt: string
}

export interface ExpenseEventView {
  eventId: string
  kind: 'status' | 'message'
  fromStatus: ExpenseStatus | null
  toStatus: ExpenseStatus | null
  message: string | null
  actorName: string | null
  createdAt: string
}

export interface ExpenseSignatureView {
  signatureId: string
  role: SignatureRole
  method: SignatureMethod
  signerName: string
  contentHash: string
  signedAt: string
}

// The immutable signed PDF made for a signature.
export interface ExpenseDocumentView {
  documentId: string
  // Whose signature it was made for: the claimant's (when they signed) or the
  // approver's (the final record).
  role: SignatureRole
  sha256: string
  sizeBytes: number
  createdAt: string
}

export interface ExpenseClaimSummary {
  claimId: string
  kind: ExpenseKind
  status: ExpenseStatus
  currency: string
  total: number
  aircraftRegistration: string | null
  purpose: string | null
  claimantName: string
  createdAt: string
  submittedAt: string | null
  decidedAt: string | null
  paidOn: string | null
}

export interface ExpenseClaimDetail extends ExpenseClaimSummary {
  userId: string
  claimantPhone: string | null
  homeAddress: string | null
  decisionNote: string | null
  lines: ExpenseLineView[]
  receipts: ExpenseReceiptView[]
  events: ExpenseEventView[]
  signatures: ExpenseSignatureView[]
  documents: ExpenseDocumentView[]
  // The hash a signature made now would cover.
  contentHash: string
  editable: boolean
  // For the claimant: what is still missing before it can be signed.
  missing: SubmitBlocker[]
}

export interface ExpenseOptions {
  rates: ExpenseRates
  // The club's calendar day, the latest a line may be dated.
  today: string
  // The aircraft a fuel claim may name: the bookable fleet.
  aircraft: string[]
  declarationVersion: number
}

// What a processor does with a submitted claim. Approving signs it, like the
// claimant did, over the hash they approved; asking and rejecting need a
// message the claimant will read.
export const ExpenseDecisionSchema = z.discriminatedUnion('action', [
  z.object({
    action: z.literal('approve'),
    contentHash: z.string().regex(/^[0-9a-f]{64}$/),
    proof: SignProofSchema,
    note: z.string().trim().max(1000).nullish(),
  }),
  z.object({ action: z.literal('reject'), message: z.string().trim().min(1).max(2000) }),
  z.object({ action: z.literal('request_info'), message: z.string().trim().min(1).max(2000) }),
])
export type ExpenseDecision = z.infer<typeof ExpenseDecisionSchema>

export const MarkPaidSchema = z.object({ paidOn: DateStringSchema.optional() })

export const AdminExpenseListQuerySchema = z.object({
  // Comma-separated statuses; absent = all.
  status: z
    .string()
    .optional()
    .refine((v) => v === undefined || v.split(',').every((s) => (EXPENSE_STATUSES as readonly string[]).includes(s)), 'Unknown status'),
  kind: ExpenseKindSchema.optional(),
  q: z.string().trim().max(100).optional(),
})

export interface ExpenseCounts {
  awaiting: number
  needsInfo: number
  approved: number
}
